// frontend/components/sidebar/CreatePageModal.tsx

"use client";

import { FileText, LayoutTemplate, Check } from "lucide-react";
import { useState, useEffect } from "react";
import { fetchTemplates, DocTemplate } from "../../utils/templates";

interface CreatePageModalProps {
  isOpen: boolean;
  parentTitle: string;
  titleValue: string;
  onTitleChange: (val: string) => void;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  // 템플릿 선택. 고르면 그 문서를 복제해 새 문서를 만든다.
  accessToken?: string;
  templateId?: string | null;
  onTemplateChange?: (id: string | null) => void;
}

// 신규 문서 생성을 위한 모달 창 렌더링
export default function CreatePageModal({
  isOpen, parentTitle, titleValue, onTitleChange, onClose, onSubmit,
  accessToken, templateId = null, onTemplateChange,
}: CreatePageModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [templates, setTemplates] = useState<DocTemplate[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setIsCreating(false);
      return;
    }
    // 열릴 때만 불러온다. 템플릿이 하나도 없으면 선택 UI 자체를 감춰
    // 기존 "제목 입력 → 생성" 흐름을 그대로 유지한다(안 쓰는 사람에게 부담을 주지 않는다).
    fetchTemplates(accessToken).then(setTemplates);
  }, [isOpen, accessToken]);

  if (!isOpen) return null;

  // 빈 제목 입력을 차단하는 유효성 검사 핸들러
  const handleSafeSubmit = async () => {
    if (titleValue.trim() === "") {
      alert("문서 제목을 입력해주세요!");
      return;
    }
    if (isCreating) return;
     
    setIsCreating(true);
    try {
      await onSubmit();
    } finally {
    setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-zinc-900 p-5 rounded-xl shadow-2xl w-80 border dark:border-zinc-800 animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
        <div className="flex items-center text-xs font-semibold text-gray-500 dark:text-zinc-400 mb-4 bg-gray-100 dark:bg-zinc-800 px-2 py-1.5 rounded w-fit">
          <FileText size={12} className="mr-1.5" />
          추가 대상 : {parentTitle}
        </div>
        
        <h3 className="text-sm font-bold text-gray-800 dark:text-zinc-200 mb-3">새 페이지 제목</h3>
        <input 
          autoFocus 
          type="text" 
          placeholder="문서 제목을 입력하세요" 
          value={titleValue} 
          onChange={e => onTitleChange(e.target.value)} 
          className="w-full p-2.5 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg bg-gray-50 dark:bg-zinc-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-[#006DBA] transition-all mb-5 disabled:opacity-50 disabled:cursor-not-allowed"
          onKeyDown={e => e.key === 'Enter' && handleSafeSubmit()} 
          disabled={isCreating}
        />
        {/* 템플릿 선택 — 있을 때만 노출 */}
        {templates.length > 0 && (
          <div className="mb-5 -mt-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-zinc-400 mb-1.5">
              <LayoutTemplate size={12} />
              템플릿
            </div>
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-zinc-700 rounded-lg divide-y divide-gray-100 dark:divide-zinc-800">
              <button
                type="button"
                onClick={() => onTemplateChange?.(null)}
                disabled={isCreating}
                className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                  templateId === null
                    ? "bg-blue-50 dark:bg-blue-500/10 text-[#006DBA] dark:text-blue-400 font-semibold"
                    : "hover:bg-gray-50 dark:hover:bg-zinc-800 text-gray-600 dark:text-zinc-300"
                }`}
              >
                <FileText size={13} className="shrink-0" />
                <span className="flex-1 truncate">빈 문서</span>
                {templateId === null && <Check size={13} className="shrink-0" />}
              </button>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTemplateChange?.(t.id)}
                  disabled={isCreating}
                  title={t.textSnippet || t.title}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                    templateId === t.id
                      ? "bg-blue-50 dark:bg-blue-500/10 text-[#006DBA] dark:text-blue-400 font-semibold"
                      : "hover:bg-gray-50 dark:hover:bg-zinc-800 text-gray-600 dark:text-zinc-300"
                  }`}
                >
                  <LayoutTemplate size={13} className="shrink-0" />
                  <span className="flex-1 truncate">{t.title || "제목 없음"}</span>
                  {templateId === t.id && <Check size={13} className="shrink-0" />}
                </button>
              ))}
            </div>
            {templateId && (
              <p className="text-[10px] text-gray-400 dark:text-zinc-500 mt-1.5">
                템플릿의 내용과 하위 페이지가 함께 복사됩니다.
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <button onClick={onClose} disabled={isCreating} className="px-4 py-2 text-xs font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">취소</button>
          <button onClick={handleSafeSubmit} disabled={isCreating} className="px-4 py-2 text-xs font-medium bg-[#006DBA] text-white rounded-lg hover:bg-[#005A9E] transition-colors disabled:opacity-70 disabled:cursor-wait">
            {isCreating ? "생성 중..." : "생성"}
          </button>
        </div>
      </div>
    </div>
  );
}